import { SettingHeader } from '@affine/component/setting-components';
import { type FC, type ReactNode } from 'react';

import * as styles from './settings-entity-page.css';
import {
  SettingsListControls,
  SettingsPagination,
} from './settings-navigation';

export const SettingsEntityPage: FC<{
  title: string;
  subtitle?: ReactNode;
  headerAction?: ReactNode;
  controls?: ReactNode;
  children: ReactNode;
}> = ({ title, subtitle, headerAction, controls, children }) => {
  return (
    <>
      <SettingHeader title={title} subtitle={subtitle} action={headerAction} />
      <div className={styles.body}>
        {controls}
        {children}
      </div>
    </>
  );
};

export const SettingsEntityCollection: FC<{
  loading: boolean;
  hasItems: boolean;
  emptyContent: ReactNode;
  loadingContent: ReactNode;
  children: ReactNode;
}> = ({ loading, hasItems, emptyContent, loadingContent, children }) => {
  if (loading) {
    return <>{loadingContent}</>;
  }

  if (!hasItems) {
    return <>{emptyContent}</>;
  }

  return <>{children}</>;
};

export const SettingsSearchableEntityPage: FC<{
  title: string;
  subtitle?: ReactNode;
  headerAction?: ReactNode;
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder: string;
  action?: ReactNode;
  banner?: ReactNode;
  loading: boolean;
  hasItems: boolean;
  loadingContent: ReactNode;
  emptyContent: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  pagination?: {
    page: number;
    pageCount: number;
    pageSize: number;
    totalItems: number;
    onPageChange: (page: number) => void;
    previousLabel: string;
    nextLabel: string;
  };
}> = ({
  title,
  subtitle,
  headerAction,
  searchValue,
  onSearchChange,
  searchPlaceholder,
  action,
  banner,
  loading,
  hasItems,
  loadingContent,
  emptyContent,
  children,
  footer,
  pagination,
}) => {
  return (
    <SettingsEntityPage
      title={title}
      subtitle={subtitle}
      headerAction={headerAction}
      controls={
        <SettingsListControls
          searchValue={searchValue}
          onSearchChange={onSearchChange}
          searchPlaceholder={searchPlaceholder}
          action={action}
        />
      }
    >
      {banner ?? null}
      <SettingsEntityCollection
        loading={loading}
        hasItems={hasItems}
        loadingContent={loadingContent}
        emptyContent={emptyContent}
      >
        {children}
      </SettingsEntityCollection>
      {pagination ? (
        <SettingsPagination
          page={pagination.page}
          pageCount={pagination.pageCount}
          pageSize={pagination.pageSize}
          totalItems={pagination.totalItems}
          onPageChange={pagination.onPageChange}
          previousLabel={pagination.previousLabel}
          nextLabel={pagination.nextLabel}
        />
      ) : null}
      {footer ?? null}
    </SettingsEntityPage>
  );
};
